import { useTranslation } from "react-i18next";
import { GlobalSchema, type Global } from "../../lib/schedule-presets/art-metadata";
import type { MetadataEventPayload } from "../../lib/metadata-history/metadata-events";
import type { BuilderDayLayout } from "../../lib/metadata-builder/day-layouts";
import { dispatchBuilderMetadata } from "../../lib/metadata-builder/edit-text";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import type { MetadataDay } from "../../lib/metadata-history/metadata-targets";
import { SlotSchema } from "../../lib/schedule-presets/art-metadata";
import { MetadataField } from "../editor-panel/developer-section/MetadataField";
import { MetadataElementLayout } from "../editor-panel/developer-section/MetadataElementLayout";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";

type GlobalElementPlacementProps = Pick<Global, "position" | "rotation"> & {
  layout?: never;
  day?: never;
  index: number;
  onUpdate: (payload: MetadataEventPayload, mergeKey: string) => void;
};

type DayElementPlacementProps = {
  layout: BuilderDayLayout;
  day: MetadataDay;
  index: number;
  position?: never;
  rotation?: never;
  onUpdate?: never;
};

type GlobalElementPlacementControlProps = GlobalElementPlacementProps | DayElementPlacementProps;

export function GlobalElementPlacementControls(props: GlobalElementPlacementControlProps) {
  const { t } = useTranslation();
  const labels = metadataLabels(t);

  if ("layout" in props && props.layout) {
    const { layout, day, index } = props;
    const element = (layout.exceptions[day] ?? layout.shared)[index];
    if (!element) return null;
    const custom = !!layout.exceptions[day];
    const group = custom ? day : "shared";
    const change = (field: "offset" | "rotation" | "scale", value: unknown) => {
      const next = structuredClone(layout);
      const target = (next.exceptions[day] ?? next.shared)[index]!;
      if (field === "offset") target.offset = SlotSchema.shape.offset.parse(value).map(Math.round);
      if (field === "rotation") target.rotation = SlotSchema.shape.rotation.parse(value) ?? 0;
      if (field === "scale" && "type" in target) target.scale = SlotSchema.shape.scale.parse(value) ?? 100;
      const action = field === "rotation" ? "rotation" : "placement";
      dispatchBuilderMetadata({ type: "day-layout/set", layout: next, styles: {}, action, day: custom ? day : undefined }, `${layout.mode}:${group}:${index}:${field}`);
    };
    const fields = [
      { key: "offset", content: <MetadataField schema={SlotSchema.shape.offset} value={element.offset}
        label={labels.offset!} labels={labels} onChange={value => change("offset", value)} /> },
      { key: "rotation", content: <MetadataField schema={SlotSchema.shape.rotation} value={element.rotation}
        label={labels.rotation!} labels={labels} onChange={value => change("rotation", value)} /> },
    ];
    if ("type" in element && element.type === "icon") fields.push({ key: "scale", content: <MetadataField
      schema={SlotSchema.shape.scale} value={element.scale} label={labels.scale!} labels={labels} stacked onChange={value => change("scale", value)} /> });
    return <div onBlurCapture={endMetadataMerge}><MetadataElementLayout fields={fields} /></div>;
  }

  const { position, rotation, index, onUpdate } = props;
  const path = `globals.${index}`;

  return <MetadataElementLayout fields={[
    { key: "position", content: <MetadataField schema={GlobalSchema.shape.position} value={position}
      label={labels.position!} labels={labels} onChange={value => {
        const point = GlobalSchema.shape.position.parse(value);
        onUpdate({ type: "position/set", path, point }, `${path}:position`);
      }} /> },
    { key: "rotation", content: <MetadataField schema={GlobalSchema.shape.rotation} value={rotation}
      label={labels.rotation!} labels={labels} onChange={value => {
        const nextRotation = GlobalSchema.shape.rotation.parse(value) ?? 0;
        onUpdate({ type: "global/rotation", index, rotation: nextRotation }, `${path}:rotation`);
      }} /> },
  ]} />;
}
